import { type QueryStatus } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useCustomerType } from '@/hooks/useCustomerType';
import { type DeletionRecord } from '@/hooks/useTenantDeletionMutations';
import { useTenantDeletions } from '@/hooks/useTenantDeletionStatus';
import { defaultFilters } from '@/sections/customerManagement/config/columnDefinitions';
import {
  useCustomerIntegrationHealth,
  useCustomerRiskCounts,
  useLastCompromiseDate,
} from '@/sections/customerManagement/hooks/data';
import { useCustomerEvmScores } from '@/sections/customerManagement/hooks/data/useCustomerEvmScores';
import { useBulkCustomerStatus } from '@/sections/customerManagement/hooks/status';
import { useCustomerDisplayName } from '@/sections/customerManagement/hooks/useCustomerDisplayName';
import {
  CustomerDataWithMetrics,
  Filters,
  Status,
} from '@/sections/customerManagement/types';
import { useAuth } from '@/state/auth';
import { CustomerTypeValue, RiskSeverity } from '@/types';
import { mergeStatus } from '@/utils/api';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

const isValidStatus = (status: unknown): status is Status => {
  return (
    typeof status === 'string' &&
    ['Active', 'Paused', 'Setup', 'Upcoming', 'Completed'].includes(status)
  );
};

export interface UseCustomerManagementOptions {
  includeTypes?: CustomerTypeValue[];
  excludeTypes?: CustomerTypeValue[];
}

const ACTIVE_DELETION_STATES: ReadonlySet<DeletionRecord['state']> = new Set([
  'requested',
  'enqueued',
  'scheduled',
  'purging',
  'verifying',
]);

// Non-terminal (active) records outrank terminal (completed/failed/cancelled)
// ones; within the same class the latest requestedAt wins. Prevents a stale
// terminal history record from shadowing an in-flight one (backend returns the
// full unordered history — deletion.go:247).
function preferDeletion(a: DeletionRecord, b: DeletionRecord): DeletionRecord {
  const aActive = ACTIVE_DELETION_STATES.has(a.state);
  const bActive = ACTIVE_DELETION_STATES.has(b.state);
  if (aActive !== bActive) return aActive ? a : b;
  return b.requestedAt > a.requestedAt ? b : a; // ISO strings sort lexicographically
}

export const useCustomerManagement = (
  options?: UseCustomerManagementOptions
) => {
  const { me, whoami, isPraetorianUser } = useAuth();
  const [filters, setFilters] = useStorage<Filters>(
    {
      queryKey: 'customerManagementFilters.v3',
      key: StorageKey.FILTERS_CUSTOMER_MANAGEMENT,
      ephemeralKeys: ['search'],
    },
    defaultFilters
  );

  // Use the accounts list from whoami as the source of truth for which
  // accounts the user can access (includes subtenant-inherited accounts).
  // Exclude the user's own personal tenant.
  const accessibleAccounts = useMemo(
    () => (whoami?.accounts ?? []).filter(email => email !== me),
    [whoami?.accounts, me]
  );

  const accountsStatus = whoami ? 'success' : ('pending' as const);

  const typeQuery = useCustomerType(true); // doNotImpersonate for customer management

  // Only show accounts that have a CustomerType — real customers added via the
  // "Add Customer" flow always have a type. Individual user collaborators do not.
  // While types are loading, show all accounts to avoid flashing empty state.
  const customerAccounts = useMemo(
    () =>
      typeQuery.status !== 'success'
        ? accessibleAccounts
        : accessibleAccounts.filter(email => typeQuery.dataMap.has(email)),
    [accessibleAccounts, typeQuery.status, typeQuery.dataMap]
  );

  const customerStatusQuery = useBulkCustomerStatus(
    customerAccounts,
    typeQuery.dataMap as Map<string, CustomerTypeValue>
  );
  const riskCountsQuery = useCustomerRiskCounts();
  const displayNameQuery = useCustomerDisplayName();
  const integrationHealthQuery = useCustomerIntegrationHealth();
  const lastCompromiseQuery = useLastCompromiseDate();
  const evmScoresQuery = useCustomerEvmScores();

  // Tenant-deletion status: ONE cross-tenant list query joined client-side per
  // row — never N+1. The list is keyed on `TenantDeletion.tenant`, which the
  // backend guarantees IS the customer account email used as the row key (the
  // tenant username IS the email; see architecture A15), so `deletion.tenant`
  // maps directly onto `email` below. Non-Praetorian users get a 403 (surfaced
  // as an empty map, not a toast); the deletion UI is Praetorian-gated in B9.
  // Gate the fetch itself on isPraetorianUser so non-Praetorian parent-tenant
  // users never fire the request that can only 403.
  const deletionsQuery = useTenantDeletions({ enabled: isPraetorianUser });
  const deletionByTenant = useMemo(() => {
    // The server lock only prevents two *active* deletions per tenant; terminal
    // records accumulate as unordered history, so precedence is required
    // (active-over-terminal, then latest requestedAt) to avoid shadowing an
    // in-flight deletion with a stale terminal one.
    const map = new Map<string, DeletionRecord>();
    for (const deletion of deletionsQuery.data ?? []) {
      const existing = map.get(deletion.tenant);
      map.set(
        deletion.tenant,
        existing ? preferDeletion(existing, deletion) : deletion
      );
    }
    return map;
  }, [deletionsQuery.data]);

  const customers = useMemo<CustomerDataWithMetrics[]>(() => {
    return customerAccounts
      .map(email => {
        const healthData = integrationHealthQuery.dataMap.get(email);
        const rawStatus = customerStatusQuery.data.get(email) || 'Setup';
        const resolvedDisplayName =
          displayNameQuery.dataMap.get(email) || email;

        return {
          email,
          displayName: resolvedDisplayName,
          status: isValidStatus(rawStatus) ? rawStatus : 'Setup',
          type: typeQuery.dataMap.get(email) || undefined,
          critical:
            riskCountsQuery.dataMap.get(`${email}#${RiskSeverity.Critical}`) ||
            0,
          high:
            riskCountsQuery.dataMap.get(`${email}#${RiskSeverity.High}`) || 0,
          medium:
            riskCountsQuery.dataMap.get(`${email}#${RiskSeverity.Medium}`) || 0,
          low: riskCountsQuery.dataMap.get(`${email}#${RiskSeverity.Low}`) || 0,
          info:
            riskCountsQuery.dataMap.get(`${email}#${RiskSeverity.Info}`) || 0,
          exposure: 0,
          healthyIntegrations: healthData?.healthyIntegrations || 0,
          totalIntegrations: healthData?.totalIntegrations || 0,
          health: healthData
            ? `${healthData.healthyIntegrations}/${healthData.totalIntegrations}`
            : '',
          riskCountsStatus: riskCountsQuery.status || 'pending',
          typeStatus: typeQuery.status || 'pending',
          integrationHealthStatus: integrationHealthQuery?.status || 'pending',
          statusStatus: customerStatusQuery.status || 'pending',
          lastCompromiseDate:
            lastCompromiseQuery.dataMap.get(email) || undefined,
          lastCompromiseStatus: lastCompromiseQuery.status || 'pending',
          subscriptionEndDate:
            customerStatusQuery.endDateMap?.get(email) || undefined,
          evm: evmScoresQuery.dataMap.get(email)?.evm ?? 0,
          dvs: evmScoresQuery.dataMap.get(email)?.dvs ?? 0,
          pas: evmScoresQuery.dataMap.get(email)?.pas ?? 0,
          evmStatus: evmScoresQuery.status,
          evmBreakdown: evmScoresQuery.dataMap.get(email),
          // tenant == email (A15): join the deletion record for this account.
          deletion: deletionByTenant.get(email),
          deletionStatus: deletionsQuery.status,
          queryStatus: {
            riskCounts: riskCountsQuery.status || 'pending',
            integrationHealth: integrationHealthQuery?.status || 'pending',
            type: typeQuery.status || 'pending',
            status: customerStatusQuery.status || 'pending',
            lastCompromise: (lastCompromiseQuery.status ||
              'pending') as QueryStatus,
          },
        };
      })
      .filter(customer => {
        if (options?.includeTypes && customer.type) {
          return options.includeTypes.includes(
            customer.type as CustomerTypeValue
          );
        }
        if (options?.excludeTypes && customer.type) {
          return !options.excludeTypes.includes(
            customer.type as CustomerTypeValue
          );
        }
        return true;
      });
  }, [
    customerAccounts,
    customerStatusQuery,
    riskCountsQuery,
    integrationHealthQuery,
    typeQuery,
    displayNameQuery,
    lastCompromiseQuery,
    evmScoresQuery,
    deletionByTenant,
    deletionsQuery.status,
    options,
  ]);

  const filteredCustomers = useMemo(() => {
    let filteredCollaborators = customers;
    if (filters.search) {
      filteredCollaborators = customers.filter(customer => {
        return (
          customer.displayName
            .toLowerCase()
            .includes(filters.search!.toLowerCase()) ||
          customer.email.toLowerCase().includes(filters.search!.toLowerCase())
        );
      });
    }
    if (filters.advancedFilters && filters.advancedFilters.status) {
      filteredCollaborators = filteredCollaborators.filter(customer => {
        return filters.advancedFilters!.status!.includes(customer.status);
      });
    }
    if (filters.advancedFilters && filters.advancedFilters.type) {
      filteredCollaborators = filteredCollaborators.filter(customer => {
        return customer.type && typeof customer.type === 'string'
          ? filters.advancedFilters!.type!.includes(customer.type)
          : false;
      });
    }
    return filteredCollaborators;
  }, [customers, filters]);

  return {
    filters,
    setFilters,
    customers: filteredCustomers,
    allCustomers: customers, // Raw unfiltered customer list
    customersStatus: mergeStatus(
      accountsStatus,
      // customerStatusQuery.status,
      displayNameQuery.status
    ),
    totalCustomers: customerAccounts.length,
  };
};
